<script setup>
import axios from 'axios';
import { computed, onUnmounted, ref, watch } from 'vue';
import { extractApiError } from '../apiError';
import {
  formatCompactMoney, formatDateTime, formatMoney, formatNumber, toNumber,
} from '../format';
import { intlLocale, t } from '../i18n';
import { PANEL } from '../ui';

// Confirmed income and an estimate including due coupons without news; month's fees covered first.
// Only the current month (up to its record date), formula and accuracy folded (docs/tpay/SPEC.md).
const props = defineProps({
  uid: {
    type: String,
    required: true,
  },
});

const RULES_URL = 'https://cdn.t-capital-funds.ru/static/documents/rules_amendments_004_TPAY.pdf';

const calc = ref(null);
const error = ref('');
const formulaOpen = ref(false);
const accuracyOpen = ref(false);
let controller;

async function load() {
  controller?.abort();
  const request = new AbortController();
  controller = request;
  error.value = '';
  calc.value = null;
  try {
    const { data } = await axios.get(`/instruments/${props.uid}/payout-calc`, { signal: request.signal });
    if (!request.signal.aborted) {
      calc.value = data;
    }
  } catch (e) {
    if (!request.signal.aborted && e?.response?.status !== 401) {
      error.value = extractApiError(e, t('tpay.failed'));
    }
  }
}

watch(() => props.uid, load, { immediate: true });
onUnmounted(() => controller?.abort());

function day(date) {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString(intlLocale(), { day: '2-digit', month: '2-digit' });
}

// Units in short form: «238,7 млн».
function compact(value) {
  const n = toNumber(value);
  return n === null ? '—' : new Intl.NumberFormat(intlLocale(), { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

function monthName(month) {
  return new Date(`${month}-15T12:00:00Z`).toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' });
}

function perUnit(value) {
  return toNumber(value) === null ? '—' : `${formatNumber(value, 4)} ₽`;
}

function rub(value) {
  return formatMoney(value, 'RUB');
}

const coupons = computed(() => (calc.value ? calc.value.credited + calc.value.expected : null));
const todayFormulaRows = computed(() => {
  if (!calc.value) {
    return [];
  }
  const today = calc.value.as_of;
  return [
    { key: 'received', label: t('tpay.receivedCoupons'), value: formatCompactMoney(today.received_coupons) },
    { key: 'fees', label: t('tpay.feesCovered'), value: `− ${formatCompactMoney(today.fees_covered)}` },
    { key: 'income', label: t('tpay.income'), value: `= ${formatCompactMoney(today.income)}` },
    { key: 'units', label: t('tpay.units'), value: `÷ ${compact(calc.value.units)}` },
    { key: 'perUnit', label: t('tpay.perUnit'), value: `= ${perUnit(today.per_unit)}` },
  ];
});

const feesNote = computed(() => {
  const source = calc.value?.fees_source;
  if (!source) {
    return '';
  }
  return source.type === 'statement'
    ? t('tpay.feesStatement', { date: day(source.date), rate: formatNumber((source.other_rate ?? 0) * 100, 3) })
    : t('tpay.feesEstimate', { rate: formatNumber(source.rate * 100, 2) });
});

const unitsNote = computed(() => {
  const source = calc.value?.units_source;
  if (!source) {
    return '';
  }
  return source.type === 'statement'
    ? t('tpay.unitsStatement', { date: day(source.date) })
    : t('tpay.unitsSnapshot', { nav: formatCompactMoney(source.nav), price: rub(source.unit_price) });
});

function sourceLabel(source) {
  if (!source) {
    return '';
  }
  if (source.type === 'trades') {
    return t('tpay.qtyTrades', { date: day(source.date) });
  }
  return source.type === 'statement'
    ? t('tpay.qtyStatement', { date: day(source.date) })
    : t('tpay.qtySnapshot', { time: formatDateTime(source.taken_at) });
}

const STATUS_CLASS = {
  credited: 'bg-emerald-500/15 text-emerald-300',
  expected: 'bg-slate-700 text-slate-200',
  after_period: 'bg-slate-800 text-slate-500',
  skipped: 'bg-amber-500/15 text-amber-300',
  unconfirmed: 'bg-slate-700 text-slate-200',
  amount_pending: 'bg-slate-700 text-slate-200',
};

const couponRows = computed(() => (calc.value?.coupons ?? []).map((c) => {
  const status = c.status === 'credited' && !c.received_confirmed
    ? (c.credit_known ? 'amount_pending' : 'unconfirmed') : c.status;
  return {
    ...c,
    status,
    key: `${c.isin}-${c.coupon_date}`,
    statusClass: STATUS_CLASS[status],
    muted: status === 'after_period' || status === 'skipped',
  };
}).reverse());

const accuracyRows = computed(() => (calc.value?.accuracy ?? []).map((a) => ({
  ...a,
  diff: a.forecast !== null ? a.forecast - a.fact : null,
})));
</script>

<template>
  <div class="space-y-3 sm:space-y-4">
    <div
      v-if="error"
      :class="PANEL"
      class="text-[0.9375rem] text-rose-300"
      role="alert"
    >
      <p>{{ error }}</p>
      <button
        type="button"
        class="mt-2 min-h-11 rounded-lg px-3 text-slate-200 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
        @click="load"
      >
        {{ t('tpay.retry') }}
      </button>
    </div>
    <div
      v-else-if="!calc"
      :class="PANEL"
      class="space-y-2"
      aria-busy="true"
    >
      <div class="h-16 rounded bg-slate-800 motion-safe:animate-pulse" />
      <div class="h-11 rounded bg-slate-800 motion-safe:animate-pulse" />
    </div>

    <template v-else>
      <!-- Today's confirmed income is available only after covering the entire month's fees. -->
      <section :class="PANEL">
        <p class="mb-2 text-[0.8125rem] tabular-nums text-slate-500">
          {{ monthName(calc.month) }} · {{ t('tpay.asOf', { date: day(calc.as_of.date) }) }}
        </p>
        <p class="text-[0.9375rem] text-slate-400 sm:text-sm">
          {{ t('tpay.accruedTitle') }}
        </p>
        <p class="mt-1 text-[1.75rem] font-semibold leading-8 tabular-nums text-slate-100">
          {{ perUnit(calc.as_of.per_unit) }}
        </p>
        <p class="mt-0.5 text-[0.8125rem] tabular-nums text-slate-400">
          {{ t('tpay.net', { rate: formatNumber(calc.tax_rate * 100, 0), value: perUnit(calc.as_of.per_unit_net) }) }}
        </p>
        <p
          v-if="calc.as_of.per_unit === null"
          class="mt-3 text-[0.8125rem] text-slate-400"
        >
          {{ t('tpay.noUnits') }}
        </p>
        <template v-else-if="calc.as_of.fees_remaining > 0">
          <p class="mt-3 text-[0.9375rem] tabular-nums text-slate-300 sm:text-sm">
            {{ t('tpay.feeProgress', { covered: formatCompactMoney(calc.as_of.fees_covered), total: formatCompactMoney(calc.fees) }) }}
          </p>
          <p class="mt-0.5 text-[0.8125rem] text-slate-400">
            {{ t('tpay.feesFirst') }}
          </p>
        </template>
        <p
          v-else
          class="mt-3 text-[0.8125rem] text-slate-400"
        >
          {{ t('tpay.feesAccounted') }}
        </p>
        <p class="mt-3 text-[0.9375rem] tabular-nums text-slate-300 sm:text-sm">
          {{ t('tpay.withUnconfirmed', { value: perUnit(calc.as_of.per_unit_with_unconfirmed) }) }}
        </p>
        <p class="mt-0.5 text-[0.8125rem] tabular-nums text-slate-400">
          {{ t('tpay.net', { rate: formatNumber(calc.tax_rate * 100, 0), value: perUnit(calc.as_of.per_unit_net_with_unconfirmed) }) }}
        </p>
        <p class="mt-3 text-[0.9375rem] tabular-nums text-slate-300 sm:text-sm">
          {{ t('tpay.monthForecast', { value: perUnit(calc.per_unit.forecast) }) }}
        </p>
        <p class="mt-0.5 text-[0.8125rem] tabular-nums text-slate-400">
          {{ t('tpay.net', { rate: formatNumber(calc.tax_rate * 100, 0), value: perUnit(calc.per_unit.forecast_net) }) }}
        </p>
        <p
          v-if="calc.snapshot"
          class="mt-3 text-[0.8125rem] tabular-nums text-slate-500"
        >
          {{ t('tpay.updated', { time: formatDateTime(calc.snapshot.last_seen_at) }) }}
        </p>
      </section>

      <!-- Formula, folded: coupons − fees ÷ units; sources and links in small print under it -->
      <section :class="PANEL">
        <button
          type="button"
          class="-my-1 flex min-h-11 w-full items-center justify-between gap-3 py-1 text-left text-[0.9375rem] font-medium text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:text-sm"
          :aria-expanded="formulaOpen"
          @click="formulaOpen = !formulaOpen"
        >
          {{ t('tpay.formula') }}
          <svg
            class="size-5 shrink-0 text-slate-400 transition"
            :class="formulaOpen ? 'rotate-180' : ''"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
        <template v-if="formulaOpen">
          <h4 class="mt-3 text-[0.9375rem] font-medium text-slate-200 sm:text-sm">
            {{ t('tpay.todayFormula') }}
          </h4>
          <dl class="mt-2 divide-y divide-slate-800 text-[0.9375rem] tabular-nums sm:text-sm">
            <div
              v-for="row in todayFormulaRows"
              :key="row.key"
              class="flex justify-between gap-3 py-2"
            >
              <dt class="min-w-0 text-slate-400">
                {{ row.label }}
              </dt>
              <dd class="shrink-0 text-right text-slate-100">
                {{ row.value }}
              </dd>
            </div>
          </dl>
          <p class="mt-2 text-[0.8125rem] leading-5 text-slate-400">
            {{ t('tpay.confirmedNote') }}
          </p>
          <dl class="mt-3 divide-y divide-slate-800 text-[0.9375rem] tabular-nums sm:text-sm">
            <div class="flex justify-between gap-3 py-2">
              <dt class="min-w-0 text-slate-400">
                {{ t('tpay.unconfirmedCoupons') }}
              </dt>
              <dd class="shrink-0 text-right text-slate-100">
                {{ formatCompactMoney(calc.as_of.unconfirmed_coupons) }}
              </dd>
            </div>
            <div class="flex justify-between gap-3 py-2">
              <dt class="min-w-0 text-slate-400">
                {{ t('tpay.withUnconfirmedPerUnit') }}
              </dt>
              <dd class="shrink-0 text-right text-slate-100">
                {{ perUnit(calc.as_of.per_unit_with_unconfirmed) }}
              </dd>
            </div>
          </dl>
          <p class="mt-2 text-[0.8125rem] leading-5 text-slate-400">
            {{ t('tpay.unconfirmedNote') }}
          </p>
          <h4 class="mt-4 text-[0.9375rem] font-medium text-slate-200 sm:text-sm">
            {{ t('tpay.forecastFormula') }}
          </h4>
          <dl class="mt-2 divide-y divide-slate-800 text-[0.9375rem] tabular-nums sm:text-sm">
            <div class="flex justify-between gap-3 py-2">
              <dt class="min-w-0 text-slate-400">
                {{ t('tpay.couponsSum') }}
                <span
                  v-if="calc.expected > 0"
                  class="block text-[0.8125rem] text-slate-500"
                >{{ t('tpay.expectedPart', { value: formatCompactMoney(calc.expected) }) }}</span>
              </dt>
              <dd class="shrink-0 text-slate-100">
                {{ formatCompactMoney(coupons) }}
              </dd>
            </div>
            <div class="flex justify-between gap-3 py-2">
              <dt class="text-slate-400">
                {{ t('tpay.fees') }}
              </dt>
              <dd class="shrink-0 text-slate-100">
                − {{ formatCompactMoney(calc.fees) }}
              </dd>
            </div>
            <div class="flex justify-between gap-3 py-2">
              <dt class="text-slate-400">
                {{ t('tpay.units') }}
              </dt>
              <dd class="shrink-0 text-slate-100">
                ÷ {{ compact(calc.units) }}
              </dd>
            </div>
            <div class="flex justify-between gap-3 py-2">
              <dt class="text-slate-300">
                {{ t('tpay.perUnit') }}
              </dt>
              <dd class="font-medium text-slate-100">
                = {{ perUnit(calc.per_unit.forecast) }}
              </dd>
            </div>
          </dl>
          <p class="mt-2 text-[0.8125rem] leading-5 text-slate-500">
            {{ t('tpay.period', {
              start: day(calc.period.start), end: day(calc.period.end),
              record: day(calc.period.last_workday), pay: day(calc.period.pay_by),
            }) }}
            <br>
            {{ t('tpay.feesLabel') }} — {{ feesNote }}<template v-if="unitsNote">
              · {{ t('tpay.unitsLabel') }} — {{ unitsNote }}
            </template>
            <br>
            <a
              :href="RULES_URL"
              target="_blank"
              rel="noopener"
              class="text-slate-300 underline"
            >{{ t('tpay.rules') }}</a>
            <template v-if="calc.statement">
              ·
              <a
                :href="calc.statement.url"
                target="_blank"
                rel="noopener"
                class="text-slate-300 underline"
              >{{ t('tpay.statement', { date: day(calc.statement.date) }) }}</a>
            </template>
          </p>
        </template>
      </section>

      <!-- Every coupon of the month -->
      <section :class="PANEL">
        <h3 class="text-[0.9375rem] font-medium text-slate-200 sm:text-sm">
          {{ t('tpay.coupons', { count: couponRows.length }) }}
        </h3>
        <p
          v-if="couponRows.length === 0"
          class="py-6 text-center text-[0.9375rem] text-slate-500"
        >
          {{ t('tpay.noCoupons') }}
        </p>
        <ul
          v-else
          class="mt-1 divide-y divide-slate-800"
        >
          <li
            v-for="row in couponRows"
            :key="row.key"
            class="py-2"
            :class="row.muted ? 'opacity-60' : ''"
          >
            <div class="flex items-start justify-between gap-3">
              <span class="min-w-0">
                <span class="block truncate text-[0.9375rem] text-slate-100 sm:text-sm">{{ row.name || row.isin }}</span>
                <span class="block text-[0.8125rem] tabular-nums text-slate-500">
                  {{ t('tpay.couponDates', { coupon: day(row.coupon_date), credit: day(row.credit_date), fix: day(row.fix_date) }) }}
                </span>
              </span>
              <span class="shrink-0 text-right">
                <span class="block text-[0.9375rem] tabular-nums text-slate-100 sm:text-sm">{{ row.sum === null ? '—' : rub(row.sum) }}</span>
                <span
                  class="mt-0.5 inline-block rounded-full px-2 py-0.5 text-[0.75rem]"
                  :class="row.statusClass"
                >{{ t(`tpay.status.${row.status}`) }}</span>
              </span>
            </div>
            <p class="mt-0.5 text-[0.8125rem] tabular-nums text-slate-500">
              {{ formatNumber(row.quantity, 0) }} × {{ row.amount === null ? '—' : rub(row.amount) }}<span v-if="row.amount_estimated"> {{ t('tpay.estimated') }}</span>
              · {{ sourceLabel(row.quantity_source) }}
            </p>
          </li>
        </ul>
      </section>

      <!-- Forecast against the fact -->
      <section
        v-if="accuracyRows.length > 0"
        :class="PANEL"
      >
        <button
          type="button"
          class="-my-1 flex min-h-11 w-full items-center justify-between gap-3 py-1 text-left text-[0.9375rem] font-medium text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:text-sm"
          :aria-expanded="accuracyOpen"
          @click="accuracyOpen = !accuracyOpen"
        >
          {{ t('tpay.accuracy') }}
          <svg
            class="size-5 shrink-0 text-slate-400 transition"
            :class="accuracyOpen ? 'rotate-180' : ''"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
        <p
          v-if="accuracyOpen"
          class="mt-2 text-[0.8125rem] text-slate-500"
        >
          {{ t('tpay.accuracyNote') }}
        </p>
        <table
          v-if="accuracyOpen"
          class="mt-2 w-full text-[0.9375rem] tabular-nums sm:text-sm"
        >
          <thead>
            <tr class="text-left text-[0.8125rem] text-slate-500">
              <th class="py-1 font-normal">
                {{ t('tpay.month') }}
              </th>
              <th class="py-1 text-right font-normal">
                {{ t('tpay.forecastCol') }}
              </th>
              <th class="py-1 text-right font-normal">
                {{ t('tpay.factCol') }}
              </th>
              <th class="py-1 text-right font-normal">
                {{ t('tpay.diffCol') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-800">
            <tr
              v-for="row in accuracyRows"
              :key="row.month"
            >
              <td class="py-1.5 text-slate-300">
                {{ monthName(row.month) }}
              </td>
              <td class="py-1.5 text-right text-slate-100">
                {{ perUnit(row.forecast) }}
              </td>
              <td class="py-1.5 text-right text-slate-100">
                {{ perUnit(row.fact) }}
              </td>
              <td
                class="py-1.5 text-right"
                :class="row.diff === null || Math.abs(row.diff) < 0.01 ? 'text-slate-400' : 'text-amber-300'"
              >
                {{ row.diff === null ? '—' : formatNumber(row.diff, 4) }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>
